import React, {
  ChangeEvent,
  forwardRef,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";

import classNames from "classnames";

import {
  EditableContent,
  EditableContentInterface,
} from "../EditableContent/EditableContent";
import {
  StyledDiv,
  StyledMultilineSpan,
  StyledTextareaSpan,
} from "../EditableContent/Styles";

export interface EditableTextInterface extends EditableContentInterface {
  /** Value to display. */
  value?: string | null;
  /** Optional callback called on enter, click outside and tab. */
  onSave?: (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  /** Optional callback called on input reset. */
  onCancel?: (e) => void;
  /** If true, setting empty value will save it, instead of revert to previous value. */
  allowEmptyString?: boolean;
  /** If true, shows a textarea instead of input for multiline editing. */
  multiline?: boolean;
  /** Children content to display */
  children?: React.ReactNode;
}

export const EditableText = forwardRef<HTMLDivElement, EditableTextInterface>(
  (
    {
      onSave,
      onCancel,
      value,
      inputProps,
      allowEmptyString,
      multiline = false,
      variant = "Body 2",
      weight,
      disabled = false,
      invalid,
      wrapRef,
      children,
      ...props
    },
    ref
  ) => {
    const [currentValue, setCurrentValue] = useState(value);
    const [prevValue, setPrevValue] = useState(value);
    const escapeRef = useRef(false);
    const intTextareaRef = useRef<HTMLTextAreaElement>(null);

    useEffect(() => {
      if (currentValue !== value) {
        setCurrentValue(value);
        setPrevValue(value);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value]);

    const handleBlur = useCallback(
      (e) => {
        if (escapeRef.current) {
          setCurrentValue(prevValue);
          escapeRef.current = false;
        } else {
          if (
            e.target.value.trim().length > 0 &&
            prevValue !== e.target.value
          ) {
            setPrevValue(e.target.value);
            setCurrentValue(e.target.value);
            typeof onSave === "function" && onSave(e);
          } else {
            !allowEmptyString
              ? setCurrentValue(prevValue)
              : typeof onSave === "function" &&
                prevValue !== e.target.value &&
                onSave(e);
          }
        }
        // Reset scroll position for textarea
        if (multiline && intTextareaRef?.current) {
          intTextareaRef.current.scrollLeft = 0;
        }
      },
      [allowEmptyString, onSave, prevValue, multiline]
    );

    const handleFocus = useCallback(
      (e) => {
        if (multiline) {
          const textarea = e.target;
          const length = textarea.value.length;
          textarea.setSelectionRange(length, length);
        }
      },
      [multiline]
    );

    const handleKeyDown = useCallback(
      (e) => {
        if (e.key === "Enter" && !multiline) {
          e.target.blur();
        }
        if (e.key === "Escape") {
          e.preventDefault();
          e.stopPropagation();
          escapeRef.current = true;
          e.target.blur();
          typeof onCancel === "function" && onCancel(e);
        }
      },
      [onCancel, multiline]
    );

    const handleChange = useCallback((e) => {
      setCurrentValue(e.target.value);
    }, []);

    if (multiline) {
      return (
        <StyledDiv ref={ref || wrapRef} {...props} $multiline={multiline}>
          <StyledMultilineSpan
            variant={variant}
            forwardedAs="span"
            weight={weight}
            $disabled={disabled}
            invalid={invalid}
            className="presentation"
            onClick={() => {
              if (!disabled && intTextareaRef.current) {
                intTextareaRef.current.focus();
              }
            }}
          >
            {children
              ? children
              : String(currentValue || "").replace(/\n/g, " ")}
          </StyledMultilineSpan>
          <StyledTextareaSpan
            ref={intTextareaRef}
            forwardedAs="textarea"
            variant={variant}
            weight={weight}
            value={currentValue ?? ""}
            onFocus={handleFocus}
            onBlur={handleBlur}
            onKeyDown={handleKeyDown}
            onChange={handleChange}
            disabled={disabled}
            invalid={invalid}
            data-form-type="other"
            placeholder={inputProps?.placeholder}
            className={classNames("c-textarea", inputProps?.className)}
            rows={3}
            {...inputProps}
          />
        </StyledDiv>
      );
    }

    return (
      <EditableContent
        {...props}
        variant={variant}
        weight={weight}
        disabled={disabled}
        invalid={invalid}
        wrapRef={wrapRef}
        ref={ref}
        inputProps={{
          ...inputProps,
          value: currentValue ?? "",
          onBlur: handleBlur,
          onKeyDown: handleKeyDown,
          onChange: handleChange,
          type: "text",
          className: classNames("c-input", inputProps?.className),
        }}
      >
        {children}
      </EditableContent>
    );
  }
);

EditableText.displayName = "EditableText";
